import { Percent } from "@silo/types";
import { ProjectionsState } from "./types";
import { match, P } from "ts-pattern";
import { AprState, asPercentValue, UtilizationState } from "@silo/utils";

interface GetProjectionStateParams {
  baseApr: Percent;
  baseApr1d: Percent;
  baseApr3d: Percent;
}

export function getProjectionsState({
  baseApr,
  baseApr1d,
  baseApr3d,
}: GetProjectionStateParams): ProjectionsState {
  const baseAprValue = asPercentValue(baseApr);
  const baseApr3dValue = asPercentValue(baseApr3d);

  const projectionDiff = baseApr3dValue - baseAprValue;

  return match(projectionDiff)
    .returnType<ProjectionsState>()
    .with(P.bigint.positive(), () => ({
      _tag: "increasing",
      baseApr1d,
      baseApr3d,
    }))
    .with(P.bigint.negative(), () => ({
      _tag: "decreasing",
      baseApr1d,
      baseApr3d,
    }))
    .otherwise(() => ({ _tag: "stable" }));
}

interface GetUtilizationStateParams {
  utilization: Percent;
  uopt: Percent;
  ucrit: Percent;
}

export function getUtilizationState({
  utilization,
  ucrit,
  uopt,
}: GetUtilizationStateParams): UtilizationState {
  const utilizationState = match(utilization)
    .returnType<UtilizationState>()
    .with(P.bigint.lte(uopt), () => "optimal")
    .with(P.bigint.gte(ucrit), () => "critical")
    .otherwise(() => "aboveOptimal");

  return utilizationState;
}

export function getUtilizationClassName(
  utilizationState: UtilizationState
): string {
  return match(utilizationState)
    .with("optimal", "aboveOptimal", () => "")
    .with("critical", () => "text-amber-300")
    .exhaustive();
}

export function getAprState(
  projectionsState: ProjectionsState,
  utilizationState: UtilizationState
): AprState | null {
  const aprState = match({ projectionsState, utilizationState })
    .returnType<AprState | null>()
    // cannot determine APR state without projections
    .with({ projectionsState: P.nullish }, () => null)
    .with({ projectionsState: { _tag: "decreasing" } }, () => "decreasing")
    .with({ utilizationState: "optimal" }, () => "stable")
    .with({ utilizationState: "aboveOptimal" }, () => "increasingSlowly")
    .with({ utilizationState: "critical" }, () => "increasingRapidly")
    .exhaustive();

  return aprState;
}

export function getAprLabel(aprState: AprState | null): string {
  return match(aprState)
    .with("stable", () => "Stable rate")
    .with("increasingSlowly", () => "Rate increasing slowly")
    .with("increasingRapidly", () => "Rate increasing rapidly")
    .with("decreasing", () => "Rate decreasing")
    .with(null, () => "--")
    .exhaustive();
}

export function getAprDescription(aprState: AprState): string {
  return match(aprState)
    .with(
      "stable",
      () =>
        "The APR is stable given the current asset utilization. Utilization changes when users deposit, borrow, or repay, causing the APR to change."
    )
    .with(
      "increasingSlowly",
      () =>
        "The APR is increasing at a slow rate in response to the utilization being outside the optimal region. This happens to attract liquidity or encourage repayments. The APR keeps increasing until the utilization drops to a point in the optimal region."
    )
    .with(
      "increasingRapidly",
      () =>
        "The APR is increasing at a rapid rate in response to the utilization being in the critical region. This happens to attract liquidity and encourage  repayments. The APR keeps increasing until the utilization drops to a point in the optimal region."
    )
    .with(
      "decreasing",
      () =>
        "The APR is decreasing to reach a stable rate. This happens in response to the asset utilization returning to the optimal region from a point above."
    )
    .exhaustive();
}
